import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Users, CalendarDays, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IMAGES, HOURS } from "@/lib/content";

const SEATING = ["Indoor", "VIP"];
const TIMES = ["5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM", "10:00 PM", "11:00 PM", "12:00 AM"];

export default function Reservations() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "8:00 PM", guests: 2, seating: "Indoor", bottle_service: false, special_requests: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const seating = params.get("seating");
    const pkg = params.get("package");
    setForm((f) => ({
      ...f,
      seating: SEATING.includes(seating) ? seating : f.seating,
      bottle_service: seating === "VIP" ? true : f.bottle_service,
      special_requests: pkg ? `Interested in: ${pkg}` : f.special_requests,
    }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/reservations", { ...form, guests: Number(form.guests) });
      setDone(true);
      toast.success("Reservation received! Check your email.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  const field = "w-full bg-neutral-100 border border-black/10 rounded-xl px-4 py-3 text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-900 transition-colors";

  return (
    <>
      <Seo title="Reserve a Table in Washington DC" description="Book your table at ChiCha Lounge DC — indoor and VIP seating with bottle service on U Street. Dinner free before 10PM." path="/reservations" image={IMAGES.lounge} />
      <div className="pt-36 md:pt-44 pb-20">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8">
          <span className="pill-badge mb-5"><span className="dot" /> Dinner free before 10PM</span>
          <h1 className="display-mega text-5xl md:text-7xl">Book a table</h1>
          <p className="text-neutral-500 text-lg mt-5 max-w-2xl">Reserve in seconds. Indoor and VIP sections available — bottle service optional.</p>

          {done ? (
            <Reveal>
              <div className="card-lux p-12 text-center mt-10">
                <CheckCircle2 size={56} className="text-neutral-900 mx-auto mb-6" />
                <h2 className="display-mega text-4xl">You're all set.</h2>
                <p className="text-neutral-500 mt-4">We've received your reservation for <span className="text-neutral-900 font-medium">{form.email}</span>. Our team will confirm your table shortly — no need to wait for an email.</p>
                <button data-testid="reservation-another" onClick={() => setDone(false)} className="btn-ghost mt-8">Make Another Reservation</button>
              </div>
            </Reveal>
          ) : (
            <form onSubmit={submit} className="card-lux p-6 md:p-8 mt-10">
              {/* OpenTable-style selector row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-6 mb-6 border-b border-black/10">
                <div>
                  <label className="text-xs font-semibold text-neutral-500 flex items-center gap-1.5 mb-2"><Users size={14} /> Party Size</label>
                  <select data-testid="res-guests" value={form.guests} onChange={(e) => set("guests", e.target.value)} className={field}>
                    {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-500 flex items-center gap-1.5 mb-2"><CalendarDays size={14} /> Date</label>
                  <input data-testid="res-date" required type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={field} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-500 flex items-center gap-1.5 mb-2"><Clock size={14} /> Time</label>
                  <select data-testid="res-time" value={form.time} onChange={(e) => set("time", e.target.value)} className={field}>
                    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="text-xs font-semibold text-neutral-500 mb-2 block">Full Name</label><input data-testid="res-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={field} placeholder="Your name" /></div>
                <div><label className="text-xs font-semibold text-neutral-500 mb-2 block">Phone</label><input data-testid="res-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={field} placeholder="(202) 555-0100" /></div>
              </div>
              <div className="mt-4"><label className="text-xs font-semibold text-neutral-500 mb-2 block">Email</label><input data-testid="res-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={field} placeholder="you@email.com" /></div>

              <div className="mt-5">
                <label className="text-xs font-semibold text-neutral-500 mb-2 block">Seating</label>
                <div className="flex gap-3">
                  {SEATING.map((s) => (
                    <button type="button" key={s} data-testid={`res-seating-${s.toLowerCase()}`} onClick={() => set("seating", s)} className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${form.seating === s ? "border-neutral-900 bg-black/[0.04] text-neutral-900" : "border-black/10 text-neutral-500"}`}>{s}</button>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer mt-5">
                <input data-testid="res-bottle" type="checkbox" checked={form.bottle_service} onChange={(e) => set("bottle_service", e.target.checked)} className="w-5 h-5 accent-neutral-900" />
                <span className="text-neutral-700">Add VIP Bottle Service</span>
              </label>

              <div className="mt-5"><label className="text-xs font-semibold text-neutral-500 mb-2 block">Special Requests</label><textarea data-testid="res-requests" rows="3" value={form.special_requests} onChange={(e) => set("special_requests", e.target.value)} className={field} placeholder="Birthday, occasion, seating preference…" /></div>

              <button data-testid="res-submit" disabled={loading} className="btn-gold w-full justify-center mt-6">
                {loading ? <Loader2 className="animate-spin" size={18} /> : null} {loading ? "Submitting…" : "Reserve Table"}
              </button>
            </form>
          )}

          <Reveal className="mt-10">
            <div className="card-lux p-6 flex flex-wrap gap-x-8 gap-y-2">
              <span className="overline w-full mb-1">Hours</span>
              {HOURS.map((h) => <span key={h.day} className="text-sm text-neutral-500"><span className="text-neutral-900 font-medium">{h.day.slice(0, 3)}</span> {h.time}</span>)}
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
